import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useHealth } from '@/hooks/useHealth'
import { healthHeadline, statusDisplay } from '@/lib/health-display'
import type { HealthCheck } from '@/lib/schemas/health'
import { TestKhButton } from '@/components/shared/TestKhButton'

export default function Health() {
  const { data, isLoading, isError, error, refetch, isFetching } = useHealth()
  const checks = data?.checks ?? []
  const headline = healthHeadline(checks)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">System health</h1>
          {isFetching && !isLoading && <span className="text-xs text-gray-400">Refreshing…</span>}
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Running checks…</p>
      ) : isError ? (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Failed to load system health.'}
          </p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="border border-gray-800 px-3 py-1 text-sm hover:bg-gray-900 hover:text-white"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <div
            className={`border px-4 py-3 text-sm font-medium ${
              headline.allNormal
                ? 'border-green-300 bg-green-50 text-green-800'
                : 'border-red-300 bg-red-50 text-red-800'
            }`}
          >
            {headline.allNormal ? '✓ All systems normal' : `✕ ${headline.text}`}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {checks.map((check) => (
              <CheckCard key={check.key} check={check} />
            ))}
          </div>

          <section className="space-y-3 border border-gray-200 p-4">
            <h2 className="text-base font-semibold">KH API connection</h2>
            <TestKhButton />
          </section>
        </>
      )}
    </div>
  )
}

function CheckCard({ check }: { check: HealthCheck }) {
  const [open, setOpen] = useState(false)
  const display = statusDisplay(check.status)

  return (
    <div className="flex flex-col gap-2 border border-gray-200 p-3">
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium">{check.label}</span>
        <span
          className={`inline-flex items-center gap-1 border px-2 py-0.5 text-xs ${display.className}`}
          title={display.label}
        >
          <span aria-hidden="true">{display.icon}</span>
          <span className="sr-only">{display.label}</span>
        </span>
      </div>

      <p className="text-sm text-gray-700">{check.summary}</p>

      <div className="mt-auto flex items-center gap-3 pt-1">
        {check.link && (
          <Link to={check.link} className="text-xs text-blue-700 hover:underline">
            View →
          </Link>
        )}
        {check.detail && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-gray-500 hover:text-black"
          >
            {open ? 'Hide details' : 'Details'}
          </button>
        )}
      </div>

      {open && check.detail && (
        <p className="border-t border-gray-100 pt-2 text-xs whitespace-pre-wrap text-gray-500">
          {check.detail}
        </p>
      )}
    </div>
  )
}
